import { CSSProperties, useEffect, useState } from 'react';
import { useDateService, DATE_FORMAT } from 'framework/utils';
import { useShowContext } from 'framework/show';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { Box, Step, StepLabel, Stepper, SxProps, Theme, Typography } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import { Badge } from 'framework/ui/mui-builder';
import { INTERACTION_ACTIONS, INTERACTION_ACTIONS_VIEW, MapedPoliciesDataType } from 'domain/shared/compliance';

interface CustomStepIconProps {
  active?: boolean;
  style?: CSSProperties;
  sx?: SxProps<Theme>;
}

const CustomStepIcon = ({ active, style }: CustomStepIconProps) => {
  return (
    <div
      style={{
        width: 8,
        height: 8,
        borderRadius: '50%',
        backgroundColor:
          style?.color || (active ? 'var(--mui-palette-success-light)' : 'var(--mui-palette-text-secondary)'),
        display: 'flex',
        zIndex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        ...style,
      }}
    />
  );
};

const ComplianceRegistryTimelineListSection = () => {
  const { data } = useShowContext();
  const { previousVersions, ...actualCompliance } = data.getData();
  const [timelines, setTimelines] = useState<MapedPoliciesDataType[]>();
  const { getMomentDateObj, formatDate } = useDateService();
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    setTimelines([actualCompliance, ...previousVersions]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const MAX_VISIBLE_NODES = 3;

  if (!timelines || timelines.length === 0) {
    return null;
  }

  const sortedContracts = [...timelines].reverse();

  const shouldCollapse = sortedContracts.length > MAX_VISIBLE_NODES;

  const formatDateToDateMonth = (dateString: string) => {
    const date = getMomentDateObj(dateString);
    return formatDate(date, DATE_FORMAT.FULL_DATE);
  };

  const formatDateToDateAndTime = (dateString: string) => {
    const date = getMomentDateObj(dateString);
    return formatDate(date, DATE_FORMAT.FULL);
  };

  const toggleExpanded = () => {
    setExpanded(!expanded);
  };

  const getVisibleContracts = () => {
    if (!shouldCollapse || expanded) {
      return sortedContracts;
    }

    const firstHalf = Math.floor(MAX_VISIBLE_NODES / 2);
    const secondHalf = MAX_VISIBLE_NODES - firstHalf - 1;

    return [
      ...sortedContracts.slice(0, firstHalf),
      { isExpandButton: true },
      ...sortedContracts.slice(sortedContracts.length - secondHalf),
    ];
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'row', py: '20px' }}>
      <Stepper
        alternativeLabel
        sx={{
          overflowX: 'auto',
          fontSize: '12px',
          '& .expand-button .MuiStepLabel-root': {
            paddingTop: '10px',
          },
          '& .MuiStepConnector-root': {
            left: 'calc(-50%)',
            right: 'calc(50%)',
            marginTop: '11px',
          },
          '& .MuiStepLabel-root': { paddingTop: '20px' },
          '& .MuiStepLabel-label': { fontSize: '12px' },
        }}
      >
        {getVisibleContracts().map((complianceInfo, index, array) => {
          if ('isExpandButton' in complianceInfo) {
            return (
              <Step
                key="expand-button"
                active={false}
                sx={{ minWidth: '250px', width: '250px', maxWidth: '250px', p: 0 }}
                className="expand-button"
              >
                <StepLabel
                  slots={{
                    stepIcon: () => (
                      <IconButton
                        onClick={toggleExpanded}
                        style={{
                          padding: 0,
                          zIndex: 1,
                          backgroundColor: 'var(--mui-palette-background-paper)',
                        }}
                      >
                        <MoreHorizIcon />
                      </IconButton>
                    ),
                  }}
                />
              </Step>
            );
          }

          const isLastStep = index === array.length - 1;

          const badgeColor = isLastStep ? 'success' : 'default';

          const createdDateFormatted = formatDateToDateMonth(complianceInfo.policyCreatedAt);

          return (
            <Step
              // eslint-disable-next-line react/no-array-index-key
              key={index}
              active={isLastStep}
              sx={{
                minWidth: '250px',
                width: '250px',
                maxWidth: '250px',
                p: 0,
                '.Mui-disabled': { cursor: 'default' },
              }}
            >
              <StepLabel
                slots={{
                  stepIcon: props => (
                    <CustomStepIcon
                      {...props}
                      sx={{
                        paddingTop: '20px',
                      }}
                    />
                  ),
                }}
              >
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <Typography variant="caption" textAlign="center" pb={2.5} mt={-5}>
                    {createdDateFormatted}
                  </Typography>
                  <Badge color={badgeColor}>v.{complianceInfo.policyVersion}</Badge>

                  <Box
                    maxHeight="100px"
                    overflow="auto"
                    sx={{
                      mt: '10px',
                    }}
                  >
                    {complianceInfo.interactionTimeline.map(item => {
                      return (
                        <Box display="flex" gap="5px">
                          <Typography
                            variant="body2"
                            color={item.action === INTERACTION_ACTIONS.SIGNED ? 'success' : ''}
                          >
                            {INTERACTION_ACTIONS_VIEW[item.action]} at
                          </Typography>
                          <Typography variant="body2">{formatDateToDateAndTime(item.createdAt)}</Typography>
                        </Box>
                      );
                    })}
                  </Box>
                </div>
              </StepLabel>
            </Step>
          );
        })}
      </Stepper>
    </Box>
  );
};

export default ComplianceRegistryTimelineListSection;
